// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {matchRoutes} from 'react-router'
import {RoutesContext} from '../react-router/use-navigate'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRouter} from '@tanstack/react-router'
import {use, useCallback} from 'react'
import {useIsTanStackRouter} from '../tanstack-router'

export interface PreloadableRoute {
  path: string
  preload(params: Record<string, string>, searchParams?: URLSearchParams): void
}

function usePreloadRouteTanStack(): (url: string) => boolean {
  const router = useRouter()

  return useCallback(
    (url: string) => {
      let pathname: string
      let searchParams: URLSearchParams

      try {
        const parsed = new URL(url, ssrSafeLocation.origin)
        pathname = parsed.pathname
        searchParams = parsed.searchParams
      } catch {
        return false
      }

      void router.preloadRoute({to: pathname, search: Object.fromEntries(searchParams)})
      return true
    },
    [router],
  )
}

function usePreloadRouteDataRouter(): (url: string) => boolean {
  const {routes} = use(RoutesContext)

  return useCallback(
    (url: string) => {
      let pathname: string
      let searchParams: URLSearchParams

      try {
        const parsed = new URL(url, ssrSafeLocation.origin)
        pathname = parsed.pathname
        searchParams = parsed.searchParams
      } catch {
        return false
      }

      const matches = matchRoutes(routes, pathname)
      if (!matches) return false

      let preloaded = false
      for (const match of matches) {
        const queryRoute = (match.route.handle as {queryRoute?: PreloadableRoute} | undefined)?.queryRoute
        if (queryRoute) {
          const params = (match.params ?? {}) as Record<string, string>
          queryRoute.preload(params, searchParams)
          preloaded = true
        }
      }

      return preloaded
    },
    [routes],
  )
}

/**
 * Returns a callback that preloads all queries for a given route by parsing the provided URL.
 *
 * In a TanStack Router context, calls `router.preloadRoute()` to preload lazy route
 * components and trigger TanStack Router loaders.
 *
 * In a Data Router context, the URL is matched against the app's registered routes
 * (from RoutesContext) to find a route with a `handle.queryRoute` that has a `preload` method.
 *
 * @returns A function that accepts a URL string and triggers preloading.
 *          Returns `true` if the URL matched a preloadable route, `false` otherwise.
 *
 * @example
 * ```tsx
 * const preload = usePreloadRoute()
 *
 * <Link to="/users/42" onMouseEnter={() => preload('/users/42')}>
 *   User 42
 * </Link>
 * ```
 */
export function usePreloadRoute(): (url: string) => boolean {
  const isTanStack = useIsTanStackRouter()
  const hook = isTanStack ? usePreloadRouteTanStack : usePreloadRouteDataRouter
  return hook()
}
